/**
 * AdminSearchConsole — Google Search Console panel on the Usage Stats page.
 * Shows a connect/setup form until a service-account key is saved, then top
 * search queries + top search landing pages for the selected range.
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle2, ExternalLink, RefreshCw, Search, Trash2, TriangleAlert } from 'lucide-react';
import api from '../lib/api';

const SETUP_STEPS = [
  ['Go to console.cloud.google.com and create (or select) a project', 'https://console.cloud.google.com/'],
  ['Enable the "Google Search Console API" (APIs & Services → Library)', null],
  ['Create a Service Account (IAM & Admin → Service Accounts) — no roles needed', null],
  ['Open the service account → Keys → Add key → JSON, and download the file', null],
  ['In Search Console → Settings → Users and permissions → Add user, paste the service account email (name@project.iam.gserviceaccount.com)', 'https://search.google.com/search-console/users'],
  ['Paste the full contents of the downloaded JSON file below and press Connect', null],
];

function QueryTable({ title, rows, keyLabel, formatter }) {
  return (
    <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6">
      <h3 className="font-display text-lg mb-4">{title}</h3>
      {(!rows || rows.length === 0) ? (
        <div className="text-muted text-sm py-6 text-center">No data for this range yet — Google reports with a ~2 day lag.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-charcoal/50 border-b border-charcoal/10">
                <th className="py-2 pr-4">{keyLabel}</th>
                <th className="py-2 pr-4 text-right">Clicks</th>
                <th className="py-2 pr-4 text-right">Impressions</th>
                <th className="py-2 pr-4 text-right">CTR</th>
                <th className="py-2 text-right">Avg position</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-charcoal/5 last:border-0">
                  <td className="py-2.5 pr-4 font-medium truncate max-w-[300px]" title={r.key}>{formatter ? formatter(r.key) : r.key}</td>
                  <td className="py-2.5 pr-4 text-right tabular-nums">{r.clicks.toLocaleString()}</td>
                  <td className="py-2.5 pr-4 text-right tabular-nums">{r.impressions.toLocaleString()}</td>
                  <td className="py-2.5 pr-4 text-right tabular-nums">{r.ctr}%</td>
                  <td className="py-2.5 text-right tabular-nums">{r.position}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminSearchConsole({ qs }) {
  const [status, setStatus] = useState(null);
  const [keyJson, setKeyJson] = useState('');
  const [prop, setProp] = useState('sc-domain:goleicestershire.com');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [queries, setQueries] = useState(null);
  const [pages, setPages] = useState(null);
  const [loadErr, setLoadErr] = useState(null);

  const loadStatus = () => api.get('/admin/gsc/status').then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  useEffect(() => { loadStatus(); }, []);

  useEffect(() => {
    if (!status?.configured) return;
    let alive = true;
    setLoadErr(null); setQueries(null); setPages(null);
    Promise.all([
      api.get(`/admin/gsc/queries?${qs}&limit=25`),
      api.get(`/admin/gsc/pages?${qs}&limit=25`),
    ]).then(([q, p]) => {
      if (!alive) return;
      setQueries(q.data); setPages(p.data);
    }).catch((e) => { if (alive) setLoadErr(e?.response?.data?.detail || 'Could not load Search Console data.'); });
    return () => { alive = false; };
  }, [qs, status?.configured]);

  const connect = async () => {
    setSaving(true); setSaveMsg(null);
    try {
      const r = await api.post('/admin/gsc/credentials', { service_account_json: keyJson, property: prop });
      setSaveMsg(r.data.verified
        ? { ok: true, text: `Connected as ${r.data.client_email} — live data verified.` }
        : { ok: false, text: `Key saved but verification failed: ${r.data.error}` });
      setKeyJson('');
      loadStatus();
    } catch (e) {
      setSaveMsg({ ok: false, text: e?.response?.data?.detail || 'Could not save the key.' });
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Search Console and delete the stored key?')) return;
    await api.delete('/admin/gsc/credentials');
    setQueries(null); setPages(null);
    loadStatus();
  };

  if (!status) return null;

  return (
    <div className="space-y-4" data-testid="gsc-section">
      <div className="flex items-baseline justify-between flex-wrap gap-2 border-t border-charcoal/10 pt-8">
        <h2 className="font-display text-3xl flex items-center gap-2"><Search size={24} className="text-heritage" /> Google Search Console</h2>
        {status.configured && (
          <div className="flex items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1.5 text-forest font-semibold" data-testid="gsc-connected-badge">
              <CheckCircle2 size={14} /> {status.property} · {status.client_email}
            </span>
            <button onClick={disconnect} data-testid="gsc-disconnect-btn" className="inline-flex items-center gap-1 text-heritage hover:underline font-semibold">
              <Trash2 size={12} /> Disconnect
            </button>
          </div>
        )}
      </div>

      {!status.configured ? (
        <div className="bg-white border border-charcoal/10 rounded-2xl p-6" data-testid="gsc-setup-panel">
          <p className="text-sm text-charcoal/80 mb-4">
            Connect Search Console to see the <strong>exact Google search queries</strong> bringing visitors in — clicks,
            impressions, click-through rate and average position — right here alongside your engagement metrics.
          </p>
          <ol className="space-y-2 text-sm mb-5 list-decimal list-inside text-charcoal/80">
            {SETUP_STEPS.map(([step, href], i) => (
              <li key={i}>
                {step}
                {href && <a href={href} target="_blank" rel="noreferrer" className="text-heritage font-semibold ml-1.5 inline-flex items-center gap-0.5 hover:underline">open <ExternalLink size={11} /></a>}
              </li>
            ))}
          </ol>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-charcoal/60 block mb-1">Search Console property</label>
              <input value={prop} onChange={(e) => setProp(e.target.value)} data-testid="gsc-property-input"
                className="w-full max-w-md border border-charcoal/20 rounded-lg px-3 py-2 text-sm font-mono" />
              <div className="text-[11px] text-muted mt-1">You have a Domain property, so keep the <code>sc-domain:</code> format.</div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-charcoal/60 block mb-1">Service-account key JSON</label>
              <textarea value={keyJson} onChange={(e) => setKeyJson(e.target.value)} rows={6} data-testid="gsc-key-textarea"
                placeholder='{"type": "service_account", "project_id": "...", "private_key": "...", "client_email": "...@....iam.gserviceaccount.com", ...}'
                className="w-full border border-charcoal/20 rounded-lg px-3 py-2 text-xs font-mono" />
              <div className="text-[11px] text-muted mt-1">Stored server-side only — never sent to visitors' browsers.</div>
            </div>
            <button onClick={connect} disabled={saving || keyJson.trim().length < 50} data-testid="gsc-connect-btn"
              className="bg-charcoal text-base px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-2">
              {saving && <RefreshCw size={13} className="animate-spin" />} Connect Search Console
            </button>
            {saveMsg && (
              <div data-testid="gsc-save-msg" className={`text-sm flex items-start gap-1.5 ${saveMsg.ok ? 'text-forest' : 'text-heritage'}`}>
                {saveMsg.ok ? <CheckCircle2 size={15} className="mt-0.5 shrink-0" /> : <TriangleAlert size={15} className="mt-0.5 shrink-0" />}
                {saveMsg.text}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {status.last_error && (
            <div className="bg-heritage/10 border border-heritage/40 rounded-xl p-3.5 text-sm text-heritage flex items-start gap-2" data-testid="gsc-error-banner">
              <TriangleAlert size={16} className="mt-0.5 shrink-0" /> {status.last_error}
            </div>
          )}
          {loadErr && !status.last_error && (
            <div className="bg-heritage/10 border border-heritage/40 rounded-xl p-3.5 text-sm text-heritage" data-testid="gsc-load-error">{loadErr}</div>
          )}
          {!queries && !loadErr ? (
            <div className="text-muted text-sm py-6 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading Search Console data…</div>
          ) : queries && (
            <>
              <div className="text-xs text-muted">
                {queries.start} → {queries.end} · Google reports with a ~2 day lag{queries.cached ? ' · cached (refreshes every 6h)' : ''}
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <QueryTable title="Top search queries" rows={queries.rows} keyLabel="Query" />
                <QueryTable title="Top search landing pages" rows={pages?.rows} keyLabel="Page"
                  formatter={(u) => u.replace(/^https?:\/\/(www\.)?[^/]+/, '') || '/'} />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
